import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

type GdprTab = 'breaches' | 'sar' | 'dpia' | 'consent';

const statusColors: Record<string, string> = {
  detected: 'bg-red-100 text-red-700', investigating: 'bg-amber-100 text-amber-700',
  contained: 'bg-blue-100 text-blue-700', notified_ico: 'bg-purple-100 text-purple-700',
  notified_subjects: 'bg-indigo-100 text-indigo-700', resolved: 'bg-green-100 text-green-700',
  closed: 'bg-slate-100 text-slate-500', received: 'bg-blue-100 text-blue-700',
  identity_verification: 'bg-amber-100 text-amber-700', processing: 'bg-cyan-100 text-cyan-700',
  extended: 'bg-orange-100 text-orange-700', completed: 'bg-green-100 text-green-700',
  refused: 'bg-red-100 text-red-700', draft: 'bg-slate-100 text-slate-600',
  in_progress: 'bg-blue-100 text-blue-700', review: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700',
  active: 'bg-green-100 text-green-700', under_review: 'bg-amber-100 text-amber-700',
  archived: 'bg-slate-100 text-slate-500',
};

export default function GdprAdmin() {
  const location = useLocation();
  const pathTab = location.pathname.split('/').pop() as GdprTab;
  const [tab, setTab] = useState<GdprTab>(['breaches','sar','dpia','consent'].includes(pathTab) ? pathTab : 'breaches');
  const [data, setData] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [timeline, setTimeline] = useState<any[]>([]);

  const tabConfig: Record<GdprTab, { title: string; endpoint: string; dashEndpoint: string; fields: string[]; createFields: { name: string; label: string; type: string; required?: boolean }[] }> = {
    breaches: {
      title: 'Breach Register', endpoint: '/api/gdpr/breaches', dashEndpoint: '/api/gdpr/breaches/dashboard',
      fields: ['breach_reference', 'title', 'severity', 'status', 'detected_at', 'ico_notification_deadline'],
      createFields: [
        { name: 'title', label: 'Breach Title', type: 'text', required: true },
        { name: 'description', label: 'Description', type: 'textarea', required: true },
        { name: 'breach_type', label: 'Type', type: 'select' },
        { name: 'severity', label: 'Severity', type: 'select' },
        { name: 'data_categories', label: 'Data Categories Affected', type: 'textarea' },
        { name: 'estimated_records_affected', label: 'Estimated Records Affected', type: 'number' },
        { name: 'systems_affected', label: 'Systems Affected', type: 'textarea' },
      ],
    },
    sar: {
      title: 'Subject Access Requests', endpoint: '/api/gdpr/sar', dashEndpoint: '/api/gdpr/sar/dashboard',
      fields: ['reference', 'requester_name', 'requester_email', 'request_type', 'status', 'deadline_at'],
      createFields: [
        { name: 'requester_name', label: 'Requester Name', type: 'text', required: true },
        { name: 'requester_email', label: 'Requester Email', type: 'text', required: true },
        { name: 'request_type', label: 'Request Type', type: 'select' },
        { name: 'description', label: 'Description', type: 'textarea' },
      ],
    },
    dpia: {
      title: 'DPIA Register', endpoint: '/api/gdpr/dpia', dashEndpoint: '/api/gdpr/dpia/dashboard',
      fields: ['reference', 'title', 'risk_level', 'status', 'created_at'],
      createFields: [
        { name: 'title', label: 'DPIA Title', type: 'text', required: true },
        { name: 'description', label: 'Description', type: 'textarea', required: true },
        { name: 'processing_description', label: 'Processing Description', type: 'textarea' },
        { name: 'necessity_justification', label: 'Necessity Justification', type: 'textarea' },
      ],
    },
    consent: {
      title: 'Consent & ROPA', endpoint: '/api/gdpr/consent/activities', dashEndpoint: '',
      fields: ['name', 'purpose', 'lawful_basis', 'data_categories', 'status'],
      createFields: [
        { name: 'name', label: 'Activity Name', type: 'text', required: true },
        { name: 'purpose', label: 'Purpose', type: 'textarea', required: true },
        { name: 'lawful_basis', label: 'Lawful Basis', type: 'select', required: true },
        { name: 'data_categories', label: 'Data Categories', type: 'textarea', required: true },
        { name: 'data_subjects', label: 'Data Subjects', type: 'textarea', required: true },
        { name: 'recipients', label: 'Recipients', type: 'textarea' },
        { name: 'retention_period', label: 'Retention Period', type: 'text' },
      ],
    },
  };

  const selectOptions: Record<string, string[]> = {
    breach_type: ['confidentiality', 'integrity', 'availability', 'combined'],
    severity: ['low', 'medium', 'high', 'critical'],
    request_type: ['access', 'rectification', 'erasure', 'restriction', 'portability', 'objection'],
    lawful_basis: ['consent', 'contract', 'legal_obligation', 'vital_interests', 'public_task', 'legitimate_interests'],
  };

  const config = tabConfig[tab];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(config.endpoint, { headers: authHeaders() });
      const d = await res.json();
      if (d.success) {
        setData(d.breaches || d.requests || d.dpias || d.activities || []);
      }
      if (config.dashEndpoint) {
        const dr = await fetch(config.dashEndpoint, { headers: authHeaders() });
        const dd = await dr.json();
        if (dd.success) setStats(dd.stats);
      }
    } catch {}
    setLoading(false);
  }, [tab]);

  useEffect(() => { fetchData(); setSelectedItem(null); setShowForm(false); }, [fetchData]);

  const handleCreate = async () => {
    try {
      const res = await fetch(config.endpoint, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(formData),
      });
      const d = await res.json();
      if (d.success) { setShowForm(false); setFormData({}); fetchData(); }
      else alert(d.message || 'Failed to create');
    } catch { alert('Failed to create'); }
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      const endpoint = tab === 'consent'
        ? `${config.endpoint}/${id}`
        : `${config.endpoint}/${id}/status`;
      await fetch(endpoint, {
        method: 'PUT', headers: authHeaders(),
        body: JSON.stringify(tab === 'consent' ? { status: newStatus } : { status: newStatus }),
      });
      fetchData();
    } catch {}
  };

  const loadDetail = async (item: any) => {
    setSelectedItem(item);
    if (tab === 'breaches' || tab === 'sar') {
      try {
        const res = await fetch(`${config.endpoint}/${item.id}`, { headers: authHeaders() });
        const d = await res.json();
        if (d.success) {
          setSelectedItem(d.breach || d.sar || item);
          setTimeline(d.timeline || []);
        }
      } catch {}
    }
  };

  const formatDate = (d: string) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
  const isOverdue = (deadline: string) => deadline && new Date(deadline) < new Date();

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">GDPR Compliance</h1>
          <p className="text-sm text-slate-500 mt-1">Data protection, breach management, and subject rights</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-slate-200 dark:border-slate-700">
        {(Object.keys(tabConfig) as GdprTab[]).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-sm font-semibold transition-colors border-b-2 -mb-px ${
              tab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}>{tabConfig[t].title}</button>
        ))}
      </div>

      {/* Dashboard Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {Object.entries(stats).filter(([k]) => k !== 'total').map(([key, value]) => (
            <div key={key} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
              <div className="text-2xl font-bold text-slate-900 dark:text-white">{String(value || 0)}</div>
              <div className="text-xs text-slate-500 capitalize">{key.replace(/_/g, ' ')}</div>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between items-center mb-4">
        <span className="text-xs text-slate-400 font-semibold">{data.length} records</span>
        <button onClick={() => { setShowForm(true); setFormData({}); }}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700">
          {tab === 'breaches' ? 'Report Breach' : tab === 'sar' ? 'Log SAR' : tab === 'dpia' ? 'New DPIA' : 'Add Activity'}
        </button>
      </div>

      {/* Create Form */}
      {showForm && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 mb-6">
          <h3 className="font-bold text-slate-900 dark:text-white mb-4">New {config.title.replace('Register', '').replace('Consent & ', '').trim()}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {config.createFields.map(f => (
              <div key={f.name} className={f.type === 'textarea' ? 'md:col-span-2' : ''}>
                <label className="block text-xs font-semibold text-slate-500 mb-1">{f.label}{f.required && ' *'}</label>
                {f.type === 'textarea' ? (
                  <textarea value={formData[f.name] || ''} onChange={e => setFormData({ ...formData, [f.name]: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white min-h-[80px]" />
                ) : f.type === 'select' ? (
                  <select value={formData[f.name] || ''} onChange={e => setFormData({ ...formData, [f.name]: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white">
                    <option value="">Select...</option>
                    {(selectOptions[f.name] || []).map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
                  </select>
                ) : (
                  <input type={f.type} value={formData[f.name] || ''} onChange={e => setFormData({ ...formData, [f.name]: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white" />
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleCreate} className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700">Save</button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">Cancel</button>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        {loading ? (
          <div className="p-12 text-sm text-slate-400">Loading...</div>
        ) : data.length === 0 ? (
          <div className="p-12">
            <div className="text-sm text-slate-400">No records yet</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  {config.fields.map(f => (
                    <th key={f} className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">{f.replace(/_/g, ' ')}</th>
                  ))}
                  <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {data.map((item: any, i: number) => (
                  <tr key={item.id || i} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 cursor-pointer" onClick={() => loadDetail(item)}>
                    {config.fields.map(f => (
                      <td key={f} className="px-4 py-3">
                        {f === 'status' || f === 'severity' || f === 'risk_level' || f === 'request_type' || f === 'lawful_basis' ? (
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${statusColors[item[f]] || 'bg-slate-100 text-slate-600'}`}>
                            {(item[f] || '').replace(/_/g, ' ')}
                          </span>
                        ) : f.includes('_at') || f.includes('deadline') ? (
                          <span className={`text-xs ${isOverdue(item[f]) ? 'text-red-600 font-bold' : 'text-slate-500'}`}>
                            {formatDate(item[f])}
                            {f === 'ico_notification_deadline' && isOverdue(item[f]) && <span className="ml-1 text-red-500">OVERDUE</span>}
                            {f === 'deadline_at' && isOverdue(item[f]) && item.status !== 'completed' && item.status !== 'refused' && <span className="ml-1 text-red-500">OVERDUE</span>}
                          </span>
                        ) : (
                          <span className="text-slate-900 dark:text-white text-xs">{String(item[f] || '-')}</span>
                        )}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-left" onClick={e => e.stopPropagation()}>
                      {tab === 'breaches' && item.status === 'detected' && (
                        <button onClick={() => handleStatusChange(item.id, 'investigating')} className="text-[10px] font-bold text-blue-600 hover:bg-blue-50 px-2 py-1 rounded">Investigate</button>
                      )}
                      {tab === 'breaches' && item.status === 'investigating' && (
                        <button onClick={() => handleStatusChange(item.id, 'contained')} className="text-[10px] font-bold text-amber-600 hover:bg-amber-50 px-2 py-1 rounded">Contain</button>
                      )}
                      {tab === 'breaches' && item.status === 'contained' && (
                        <button onClick={() => handleStatusChange(item.id, 'notified_ico')} className="text-[10px] font-bold text-purple-600 hover:bg-purple-50 px-2 py-1 rounded">Notify ICO</button>
                      )}
                      {tab === 'sar' && item.status === 'received' && (
                        <button onClick={() => handleStatusChange(item.id, 'processing')} className="text-[10px] font-bold text-blue-600 hover:bg-blue-50 px-2 py-1 rounded">Process</button>
                      )}
                      {tab === 'dpia' && item.status === 'draft' && (
                        <button onClick={() => handleStatusChange(item.id, 'in_progress')} className="text-[10px] font-bold text-blue-600 hover:bg-blue-50 px-2 py-1 rounded">Start</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Panel */}
      {selectedItem && (tab === 'breaches' || tab === 'sar') && (
        <div className="mt-6 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="font-bold text-slate-900 dark:text-white">{selectedItem.title || selectedItem.requester_name}</h3>
              <p className="text-xs text-slate-400 mt-0.5">{selectedItem.breach_reference || selectedItem.reference}</p>
            </div>
            <button onClick={() => setSelectedItem(null)} className="text-slate-400 hover:text-slate-600 p-1">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">{selectedItem.description}</p>

          {timeline.length > 0 && (
            <div>
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Timeline</h4>
              <div className="space-y-2">
                {timeline.map((t: any, i: number) => (
                  <div key={i} className="flex gap-3 text-xs">
                    <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                    <div>
                      <span className="font-semibold text-slate-900 dark:text-white">{t.action}</span>
                      {t.detail && <span className="text-slate-500 ml-2">{t.detail}</span>}
                      <div className="text-slate-400 mt-0.5">{formatDate(t.performed_at)} {t.first_name ? `by ${t.first_name} ${t.last_name}` : ''}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
